import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  Toggle,
  ErrorBanner,
  formatDateTime,
  type BadgeColor,
} from '../components/ui';

interface Workflow {
  id: string;
  name: string;
  triggerType: string;
  triggerConfig?: Record<string, unknown>;
  status: 'active' | 'paused' | 'draft';
  createdAt: string;
  updatedAt?: string;
}

interface Step {
  id: string;
  type: 'send_email' | 'send_sms' | 'wait' | 'add_tag' | 'remove_tag' | 'create_task' | 'update_field';
  config: Record<string, unknown>;
}

const triggerOptions = [
  { value: 'contact.created', label: 'New contact created' },
  { value: 'booking.created', label: 'Booking created' },
  { value: 'invoice.paid', label: 'Invoice paid' },
  { value: 'invoice.overdue', label: 'Invoice overdue' },
  { value: 'tag.added', label: 'Tag added' },
  { value: 'form.submitted', label: 'Form submitted' },
  { value: 'opportunity.stage_changed', label: 'Opportunity stage changed' },
  { value: 'review.received', label: 'Review received' },
];

const stepLibrary: { type: Step['type']; label: string; icon: string }[] = [
  { type: 'send_email', label: 'Send email', icon: '✉' },
  { type: 'send_sms', label: 'Send SMS', icon: '☎' },
  { type: 'wait', label: 'Wait', icon: '⏱' },
  { type: 'add_tag', label: 'Add tag', icon: '◈' },
  { type: 'remove_tag', label: 'Remove tag', icon: '◇' },
  { type: 'create_task', label: 'Create task', icon: '✓' },
  { type: 'update_field', label: 'Update field', icon: '✎' },
];

const statusColor: Record<Workflow['status'], BadgeColor> = {
  active: 'green',
  paused: 'yellow',
  draft: 'gray',
};

export function WorkflowsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api.get<{ data: Workflow[] }>('/workflows').catch(() => ({ data: [] })),
  });

  const workflows = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Workflows"
        subtitle={`${workflows.length} automations`}
        action={<Button onClick={() => setShowCreate(true)}>+ New workflow</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : workflows.length === 0 ? (
        <Card>
          <EmptyState
            title="No workflows yet"
            description="Automate your follow-ups, reminders, and outreach."
            action={<Button onClick={() => setShowCreate(true)}>+ New workflow</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {workflows.map((w) => (
            <Card key={w.id} onClick={() => setEditingId(w.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-base)', fontWeight: 600 }}>{w.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    Trigger: {triggerOptions.find((t) => t.value === w.triggerType)?.label ?? w.triggerType}
                    {' · '}
                    Updated {formatDateTime(w.updatedAt ?? w.createdAt)}
                  </div>
                </div>
                <Badge color={statusColor[w.status]}>{w.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <WorkflowEditor
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['workflows'] });
            setShowCreate(false);
          }}
        />
      )}

      {editingId && (
        <WorkflowEditor
          workflowId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['workflows'] });
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}

function WorkflowEditor({
  workflowId,
  onClose,
  onSaved,
}: {
  workflowId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = useQuery({
    queryKey: ['workflows', workflowId],
    queryFn: () => api.get<Workflow>(`/workflows/${workflowId}`),
    enabled: !!workflowId,
  });

  const wf = existing.data;
  const [name, setName] = useState(wf?.name ?? '');
  const [triggerType, setTriggerType] = useState(wf?.triggerType ?? 'contact.created');
  const [status, setStatus] = useState<Workflow['status']>(wf?.status ?? 'draft');
  const [steps, setSteps] = useState<Step[]>(
    ((wf?.triggerConfig?.steps as Step[]) ?? []).length > 0
      ? (wf!.triggerConfig!.steps as Step[])
      : []
  );
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        name,
        triggerType,
        triggerConfig: { steps },
        status,
      };
      return workflowId ? api.patch(`/workflows/${workflowId}`, body) : api.post('/workflows', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/workflows/${workflowId}`),
    onSuccess: onSaved,
  });

  function addStep(type: Step['type']) {
    const id = `step_${Math.random().toString(36).slice(2, 10)}`;
    const defaults: Record<Step['type'], Record<string, unknown>> = {
      send_email: { subject: '', body: '' },
      send_sms: { message: '' },
      wait: { minutes: 60 },
      add_tag: { tag: '' },
      remove_tag: { tag: '' },
      create_task: { title: '' },
      update_field: { field: '', value: '' },
    };
    setSteps((s) => [...s, { id, type, config: defaults[type] }]);
  }

  function updateStep(id: string, patch: Partial<Step>) {
    setSteps((s) => s.map((st) => (st.id === id ? { ...st, ...patch, config: { ...st.config, ...(patch.config ?? {}) } } : st)));
  }

  function removeStep(id: string) {
    setSteps((s) => s.filter((st) => st.id !== id));
  }

  function moveStep(id: string, dir: -1 | 1) {
    setSteps((s) => {
      const idx = s.findIndex((st) => st.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= s.length) return s;
      const next = s.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  if (workflowId && existing.isLoading) {
    return (
      <Modal onClose={onClose} title="Workflow">
        <Spinner />
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={workflowId ? 'Edit workflow' : 'New workflow'} width={780}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Name *">
            <Input value={name} onChange={setName} placeholder="Welcome new clients" autoFocus />
          </Field>
          <Field label="Status">
            <Select
              value={status}
              onChange={setStatus}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
              ]}
            />
          </Field>
        </div>

        <Field label="Trigger">
          <Select value={triggerType} onChange={setTriggerType} options={triggerOptions} />
        </Field>

        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            Steps
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {steps.map((step, idx) => (
              <Card key={step.id} style={{ padding: 'var(--space-3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                  <strong style={{ fontSize: 'var(--text-sm)' }}>
                    {idx + 1}. {stepLibrary.find((s) => s.type === step.type)?.label}
                  </strong>
                  <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                    <Button size="sm" variant="ghost" onClick={() => moveStep(step.id, -1)}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => moveStep(step.id, 1)}>
                      ↓
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeStep(step.id)}>
                      ×
                    </Button>
                  </div>
                </div>
                <StepConfig step={step} onChange={(config) => updateStep(step.id, { config })} />
              </Card>
            ))}
            {steps.length === 0 && (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', margin: 0 }}>
                No steps yet. Add one below.
              </p>
            )}
          </div>
          <div style={{ marginTop: 'var(--space-2)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {stepLibrary.map((s) => (
              <Button key={s.type} size="sm" variant="secondary" onClick={() => addStep(s.type)}>
                {s.icon} {s.label}
              </Button>
            ))}
          </div>
        </div>

        <ErrorBanner error={error} />

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {workflowId ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save workflow'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function StepConfig({ step, onChange }: { step: Step; onChange: (config: Record<string, unknown>) => void }) {
  function set(key: string, value: unknown) {
    onChange({ ...step.config, [key]: value });
  }

  switch (step.type) {
    case 'send_email':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <Input value={String(step.config.subject ?? '')} onChange={(v) => set('subject', v)} placeholder="Subject" />
          <Textarea value={String(step.config.body ?? '')} onChange={(v) => set('body', v)} placeholder="Email body" rows={3} />
        </div>
      );
    case 'send_sms':
      return (
        <Textarea value={String(step.config.message ?? '')} onChange={(v) => set('message', v)} placeholder="SMS message" rows={2} />
      );
    case 'wait':
      return (
        <Field label="Wait (minutes)">
          <Input value={Number(step.config.minutes ?? 60)} onChange={(v) => set('minutes', Number(v) || 0)} type="number" min={1} />
        </Field>
      );
    case 'add_tag':
    case 'remove_tag':
      return <Input value={String(step.config.tag ?? '')} onChange={(v) => set('tag', v)} placeholder="Tag name" />;
    case 'create_task':
      return <Input value={String(step.config.title ?? '')} onChange={(v) => set('title', v)} placeholder="Task title" />;
    case 'update_field':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <Input value={String(step.config.field ?? '')} onChange={(v) => set('field', v)} placeholder="Field key" />
          <Input value={String(step.config.value ?? '')} onChange={(v) => set('value', v)} placeholder="New value" />
        </div>
      );
  }
}
